import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useOptionalRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import useTracker from 'src/lib/js/hooks/useTracker'
import {
  MarketingSubscriptionChannel,
  SubscriptionSource,
  useMarketing
} from 'public/components/online_ordering/MarketingContext'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { alertError, alertSuccess } from 'shared/js/alertUtils'
import { t, Trans } from 'src/translations'
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import LoadingSpinnerOverlay from 'shared/components/common/loading_spinner/LoadingSpinnerOverlay'
import Button from 'shared/components/common/button'
import ToggleSwitch from 'shared/components/common/forms/ToggleSwitch'
import { ArrowBackIcon, ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import { ToastTOSAndPrivacy } from 'public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import {
  useMarketingAccountsQuery,
  useMarketingSubscriptionStatusQuery
} from 'src/apollo/onlineOrdering'
import { formatPhoneNumberForDisplay } from 'shared/js/phoneUtils'

export const CommunicationPreferencesModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const restaurantContext = useOptionalRestaurant()
  const restaurantName = restaurantContext?.restaurant.name ?? 'this restaurant'
  const rxGuid = restaurantContext?.ooRestaurant?.guid
  const managementSetGuid = restaurantContext?.restaurant.externalId
  const { customer } = useCustomer()
  const tracker = useTracker()
  const [loading, setLoading] = useState(false)
  const [smsAccountNumber, setSmsAccountNumber] = useState<
    string | null | undefined
  >()
  const [subscribedToEmailStatus, setSubscribedToEmailStatus] = useState(false)
  const [subscribedToSmsStatus, setSubscribedToSmsStatus] = useState(false)
  const {
    isOpen: confirmModalIsOpen,
    onClose: onConfirmModalClose,
    onOpen: onConfirmModalOpen
  } = useModal()

  const { smsAccountEnabled, subscribeToMarketing, unsubscribeFromMarketing } =
    useMarketing()

  const { data: subscriptionStatusData, refetch: refetchSubscriptionStatus } =
    useMarketingSubscriptionStatusQuery({
      variables: {
        input: {
          managementSetGuid: managementSetGuid ?? '',
          phone: customer?.phone ? `+${customer.phone}` : '',
          email: customer?.email
        }
      },
      skip: !managementSetGuid || !customer?.phone
    })

  useEffect(() => {
    const marketingSubscriptionStatus =
      subscriptionStatusData?.marketing.marketingSubscriptionStatus
    if (
      marketingSubscriptionStatus?.__typename ===
      'MarketingSubscriptionStatusResponse'
    ) {
      setSubscribedToEmailStatus(
        marketingSubscriptionStatus?.subscribedToEmail ?? false
      )
      setSubscribedToSmsStatus(marketingSubscriptionStatus.subscribedToSms)
    }
  }, [subscriptionStatusData?.marketing.marketingSubscriptionStatus])

  const handleRefetch = useCallback(async () => {
    refetchSubscriptionStatus({
      input: {
        managementSetGuid: managementSetGuid ?? '',
        phone: customer?.phone ? `+${customer.phone}` : '',
        email: customer?.email
      }
    }).then((result) => {
      const updatedStatus = result.data.marketing.marketingSubscriptionStatus
      if (updatedStatus.__typename === 'MarketingSubscriptionStatusResponse') {
        setSubscribedToEmailStatus(updatedStatus?.subscribedToEmail ?? false)
        setSubscribedToSmsStatus(updatedStatus?.subscribedToSms)
      }
    })
  }, [
    customer?.email,
    customer?.phone,
    managementSetGuid,
    refetchSubscriptionStatus
  ])

  const { data: accountData } = useMarketingAccountsQuery({
    variables: {
      input: {
        channel: 'SMS',
        managementSetGuid
      }
    },
    skip: !smsAccountEnabled
  })

  useEffect(() => {
    const marketingAccounts = accountData?.marketing.marketingAccounts
    if (
      marketingAccounts?.__typename ===
        'MarketingAccountsQuerySuccessResponse' &&
      marketingAccounts.accounts.length > 0
    ) {
      setSmsAccountNumber(marketingAccounts.accounts[0]?.sender)
    }
  }, [accountData?.marketing.marketingAccounts])

  const [currentSmsValue, setCurrentSmsValue] = useState(subscribedToSmsStatus)
  const [currentEmailValue, setCurrentEmailValue] = useState(
    subscribedToEmailStatus
  )
  const [
    currentMarketingPreferenceScreen,
    setCurrentMarketingPreferenceScreen
  ] = useState<'sms' | 'email' | undefined>(undefined)

  useEffect(() => {
    setCurrentSmsValue(subscribedToSmsStatus)
    setCurrentEmailValue(subscribedToEmailStatus)
  }, [subscribedToSmsStatus, subscribedToEmailStatus])

  const updateMarketingPreference = useCallback(
    async (
      unsubscribe: boolean,
      channel: MarketingSubscriptionChannel,
      customerInput: string,
      firstName?: string | null,
      lastName?: string | null
    ) => {
      const mutation = unsubscribe
        ? unsubscribeFromMarketing
        : subscribeToMarketing
      const success = await mutation({
        channel,
        customerInput,
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        source: SubscriptionSource.ACCOUNT_PAGE
      })
      if (success) {
        tracker.track(
          channel === MarketingSubscriptionChannel.SMS
            ? TrackingEvents.SMS_MARKETING_SIGNUP
            : TrackingEvents.EMAIL_MARKETING_SIGNUP,
          {
            restaurant_guid: rxGuid
          }
        )
        if (channel === MarketingSubscriptionChannel.SMS) {
          if (smsAccountNumber) {
            alertSuccess(
              t(
                `communication-modal.successfully-${
                  unsubscribe ? 'un' : ''
                }subscribed.sms-with-phone`,
                {
                  phone: formatPhoneNumberForDisplay(smsAccountNumber)
                }
              ),
              { autoClose: false, closeOnClick: false } // keep open so guest can read the phone number)
            )
          } else {
            alertSuccess(
              t(
                `communication-modal.successfully-${
                  unsubscribe ? 'un' : ''
                }subscribed.sms`
              )
            )
          }
          setCurrentSmsValue(!unsubscribe)
        } else {
          if (unsubscribe) {
            alertSuccess(
              t('communication-modal.successfully-unsubscribed.email')
            )
          } else {
            alertSuccess(t('communication-modal.successfully-subscribed.email'))
          }
          setCurrentEmailValue(!unsubscribe)
        }
      } else {
        alertError(t('communication-modal.unsuccessful-update'))
      }
    },
    [
      unsubscribeFromMarketing,
      subscribeToMarketing,
      tracker,
      rxGuid,
      smsAccountNumber
    ]
  )

  const updateMarketingPreferences = useCallback(async () => {
    setLoading(true)
    if (subscribedToSmsStatus !== currentSmsValue && customer?.phone) {
      await updateMarketingPreference(
        !currentSmsValue,
        MarketingSubscriptionChannel.SMS,
        customer.phone,
        customer.firstName,
        customer.lastName
      )
    }

    if (subscribedToEmailStatus !== currentEmailValue && customer?.email) {
      await updateMarketingPreference(
        !currentEmailValue,
        MarketingSubscriptionChannel.EMAIL,
        customer.email,
        customer.firstName,
        customer.lastName
      )
    }
    setLoading(false)
    await handleRefetch()
    setCurrentMarketingPreferenceScreen(undefined)
    onClose()
  }, [
    subscribedToSmsStatus,
    currentSmsValue,
    customer?.phone,
    customer?.email,
    customer?.firstName,
    customer?.lastName,
    subscribedToEmailStatus,
    currentEmailValue,
    handleRefetch,
    onClose,
    updateMarketingPreference
  ])

  const onClickSaveChanges = useCallback(async () => {
    const isUnsubscribing =
      (currentMarketingPreferenceScreen === 'sms' && !currentSmsValue) ||
      (currentMarketingPreferenceScreen === 'email' && !currentEmailValue)
    if (isUnsubscribing) {
      onConfirmModalOpen()
    } else {
      await updateMarketingPreferences()
    }
  }, [
    currentEmailValue,
    currentMarketingPreferenceScreen,
    currentSmsValue,
    onConfirmModalOpen,
    updateMarketingPreferences
  ])

  const canUpdate = useMemo(
    () =>
      currentSmsValue !== subscribedToSmsStatus ||
      currentEmailValue !== subscribedToEmailStatus,
    [
      currentSmsValue,
      subscribedToSmsStatus,
      currentEmailValue,
      subscribedToEmailStatus
    ]
  )

  const onCancel = () => {
    setCurrentMarketingPreferenceScreen(undefined)
    setCurrentSmsValue(subscribedToSmsStatus)
    setCurrentEmailValue(subscribedToEmailStatus)
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onCancel()
          onClose()
        }}
      >
        <ModalOverlay fadeIn={false} />
        <ModalContent contentClassName='modalContentV2 fullScreenMobile accountPageModal'>
          {loading && <LoadingSpinnerOverlay />}
          <div className='modalHeader'>
            {currentMarketingPreferenceScreen && (
              <button className='backButton' onClick={onCancel}>
                <ArrowBackIcon
                  accessibility='semantic'
                  aria-label={t('communication-modal.back-button')}
                />
              </button>
            )}
            <div>
              {t(
                `communication-modal.${
                  currentMarketingPreferenceScreen === 'sms'
                    ? 'sms-marketing'
                    : currentMarketingPreferenceScreen === 'email'
                    ? 'email-marketing'
                    : 'communication'
                }`
              )}
            </div>
            <ModalCloseButton />
          </div>
          <div className='modalBody'>
            <div className='accountSettingsModalContent'>
              {currentMarketingPreferenceScreen === undefined ? (
                <div className='communicationPreferencesLandingScreen'>
                  <div className='marketingPreferencesHeader'>
                    {t('communication-modal.marketing-preferences-header')}
                  </div>
                  <div className='marketingPreferencesContent'>
                    {t('communication-modal.marketing-preferences-content', {
                      restaurantName
                    })}
                  </div>
                  <div className='marketingPreferenceToggles'>
                    {smsAccountEnabled && customer?.phone && (
                      <MarketingPreferenceButton
                        text={t('communication-modal.sms-marketing')}
                        subscribed={subscribedToSmsStatus}
                        onClick={() =>
                          setCurrentMarketingPreferenceScreen('sms')
                        }
                      />
                    )}
                    {customer?.email && (
                      <MarketingPreferenceButton
                        text={t('communication-modal.email-marketing')}
                        subscribed={subscribedToEmailStatus}
                        onClick={() =>
                          setCurrentMarketingPreferenceScreen('email')
                        }
                      />
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {currentMarketingPreferenceScreen === 'sms' ? (
                    <ToggleSwitch
                      id='smsMarketingToggle'
                      label={
                        <span>
                          <Trans
                            i18nKey='communication-modal.sms'
                            components={{
                              span: (
                                <span
                                  style={
                                    customer?.phone
                                      ? { fontWeight: 'bold' }
                                      : undefined
                                  }
                                />
                              )
                            }}
                            values={{
                              phone: customer?.phone
                                ? formatPhoneNumberForDisplay(
                                    customer.phone,
                                    true
                                  )
                                : 'me'
                            }}
                          />
                        </span>
                      }
                      checked={currentSmsValue}
                      onChange={() => setCurrentSmsValue(!currentSmsValue)}
                    />
                  ) : (
                    <ToggleSwitch
                      id='emailMarketingToggle'
                      label={
                        <span>
                          <Trans
                            i18nKey='communication-modal.email'
                            components={{
                              span: (
                                <span
                                  style={
                                    customer?.email
                                      ? { fontWeight: 'bold' }
                                      : undefined
                                  }
                                />
                              )
                            }}
                            values={{ email: customer?.email ?? 'me' }}
                          />
                        </span>
                      }
                      checked={currentEmailValue}
                      onChange={() => setCurrentEmailValue(!currentEmailValue)}
                    />
                  )}
                  <div className='legalText'>
                    <Trans
                      i18nKey={`communication-modal.${currentMarketingPreferenceScreen}-terms`}
                      components={{
                        br: <br />,
                        ToastTOSAndPrivacy: <ToastTOSAndPrivacy />
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            {currentMarketingPreferenceScreen && canUpdate && (
              <div className='footer'>
                <Button onClick={onCancel} className='cancelButton'>
                  {t('communication-modal.cancel')}
                </Button>
                <Button onClick={onClickSaveChanges} className='updateButton'>
                  {t('communication-modal.save-changes')}
                </Button>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
      <CommunicationConfirmModal
        isOpen={confirmModalIsOpen}
        onClose={onConfirmModalClose}
        onConfirm={updateMarketingPreferences}
      />
    </>
  )
}

const MarketingPreferenceButton = ({
  text,
  subscribed,
  onClick
}: {
  text: string
  subscribed: boolean
  onClick: () => void
}) => {
  return (
    <Button onClick={onClick}>
      {text}
      <div className='badgeAndChevron'>
        <div
          className={classnames('statusBadge', subscribed ? 'subscribed' : '')}
        >
          {t(`communication-modal.${subscribed ? 'on' : 'off'}`)}
        </div>
        <ChevronRightIcon accessibility='decorative' />
      </div>
    </Button>
  )
}

const CommunicationConfirmModal = ({
  isOpen,
  onClose,
  onConfirm
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay fadeIn={false} />
      <ModalContent contentClassName='modalContentV2 accountPageModal communicationConfirmModal'>
        <div className='modalBody'>
          <div className='accountSettingsModalContent'>
            <div className='confirmHeader'>
              {t(
                'communication-modal.confirm-modal.update-marketing-preferences'
              )}
            </div>
            <div className='confirmBody'>
              {t('communication-modal.confirm-modal.are-you-sure')}
            </div>
          </div>
          <div className='footer stacked'>
            <button className='cancelButton' onClick={onClose}>
              {t('communication-modal.confirm-modal.keep-preferences')}
            </button>
            <button
              onClick={() => {
                onConfirm()
                onClose()
              }}
            >
              {t('communication-modal.confirm-modal.continue')}
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
